import { useState } from 'react';

interface GMBSidebarProps {
  activeSection: string;
  setActiveSection: (s: string) => void;
  darkMode: boolean;
}

export default function GMBSidebar({ activeSection, setActiveSection, darkMode }: GMBSidebarProps) {
  const [expandedSection, setExpandedSection] = useState<string | null>('strategy');

  const sections = [
    {
      id: 'strategy',
      name: 'Strategy',
      icon: 'ri-lightbulb-line',
      mainRoute: 'strategy-overview',
      items: [
        { id: 'strategy-overview', name: 'Strategy Overview', icon: 'ri-dashboard-line' },
        { id: 'as-is-state', name: 'As-Is State', icon: 'ri-file-list-line' },
        { id: 'goal-setting', name: 'Goal Setting', icon: 'ri-flag-line' },
        { id: 'action-plan', name: 'Action Plan', icon: 'ri-task-line' },
      ],
    },
    {
      id: 'implementation',
      name: 'Implementation',
      icon: 'ri-tools-line',
      mainRoute: 'implementation-overview',
      items: [
        { id: 'implementation-overview', name: 'Implementation Overview', icon: 'ri-dashboard-line' },
        { id: 'photo-upload', name: 'Photo Upload', icon: 'ri-image-line' },
        { id: 'post-scheduler', name: 'Post Scheduler', icon: 'ri-article-line' },
        { id: 'review-management', name: 'Review Management', icon: 'ri-star-line' },
        { id: 'qa-checklist', name: 'Q&A Management', icon: 'ri-question-answer-line' },
      ],
    },
    {
      id: 'governance',
      name: 'Governance',
      icon: 'ri-bar-chart-line',
      mainRoute: 'governance-overview',
      items: [
        { id: 'governance-overview', name: 'Governance Overview', icon: 'ri-dashboard-line' },
        { id: 'live-preview', name: 'Live Profile Review', icon: 'ri-eye-line' },
        { id: 'activity-log', name: 'Activity Log', icon: 'ri-history-line' },
        { id: 'alerts-permissions', name: 'Alerts & Notifications', icon: 'ri-shield-check-line' },
        { id: 'publishing-monitoring', name: 'Publishing & Activity', icon: 'ri-bar-chart-grouped-line' },
      ],
    },
    {
      id: 'optimization',
      name: 'Optimization',
      icon: 'ri-line-chart-line',
      mainRoute: 'search-performance-trends',
      items: [
        { id: 'search-performance-trends', name: 'Search Performance', icon: 'ri-search-line' },
        { id: 'review-reputation-insights', name: 'Review & Reputation', icon: 'ri-star-line' },
        { id: 'content-engagement-performance', name: 'Content & Engagement', icon: 'ri-article-line' },
        { id: 'monthly-optimization-action-plan', name: 'Monthly Optimization', icon: 'ri-calendar-check-line' },
      ],
    },
  ];

  const toggleSection = (sectionId: string, mainRoute?: string) => {
    if (mainRoute) {
      setActiveSection(mainRoute);
    }
    setExpandedSection(expandedSection === sectionId ? '' : sectionId);
  };

  const isSectionActive = (sectionId: string, mainRoute: string) => {
    if (activeSection === mainRoute) return true;
    const section = sections.find((s) => s.id === sectionId);
    if (!section) return false;
    return section.items.some((i) => i.id === activeSection);
  };

  return (
    <div className={`w-64 ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border-r h-full flex flex-col flex-shrink-0`}>
      <div className="p-6 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-teal-400 to-teal-600 rounded-lg flex items-center justify-center">
            <span className="text-white font-bold text-lg">4</span>
          </div>
          <div>
            <h1 className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>4sight</h1>
            <p className="text-xs text-gray-500 dark:text-gray-400">GMB Manager</p>
          </div>
        </div>
      </div>

      <nav className="flex-1 p-4 overflow-y-auto">
        <div className="space-y-2">
          {sections.map((section) => (
            <div key={section.id}>
              <button
                onClick={() => toggleSection(section.id, section.mainRoute)}
                className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg transition-colors text-left ${
                  isSectionActive(section.id, section.mainRoute)
                    ? darkMode
                      ? 'bg-gray-700 text-white'
                      : 'bg-gray-100 text-gray-900'
                    : darkMode
                      ? 'text-gray-400 hover:bg-gray-700 hover:text-white'
                      : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <div className="flex items-center gap-3">
                  <i className={`${section.icon} text-lg w-5 h-5 flex items-center justify-center`}></i>
                  <span className="font-medium text-sm">{section.name}</span>
                </div>
                {expandedSection === section.id ? (
                  <i className="ri-arrow-up-s-line text-sm"></i>
                ) : (
                  <i className="ri-arrow-down-s-line text-sm"></i>
                )}
              </button>

              {expandedSection === section.id && (
                <div className="mt-1 space-y-0.5">
                  {section.items.map((item) => (
                    <button
                      key={item.id}
                      onClick={() => setActiveSection(item.id)}
                      className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors ${
                        activeSection === item.id
                          ? 'bg-teal-600 text-white'
                          : darkMode
                            ? 'text-gray-400 hover:bg-gray-700 hover:text-white'
                            : 'text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      {item.icon && <i className={`${item.icon} text-sm w-4 h-4 flex items-center justify-center`}></i>}
                      <span className="text-sm">{item.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </nav>
    </div>
  );
}
